import { useState } from "react";
import { Smartphone, Globe, Shield, Zap, Star, ExternalLink, Info, Signal, Wifi } from "lucide-react";

const TravelEsim = () => {
  const providers = [
    {
      name: "Airalo",
      url: "https://www.airalo.com",
      description: "Most trusted eSIM marketplace. 200+ countries, instant activation.",
      tag: "Most Trusted",
      tagColor: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      iconBg: "bg-gradient-to-br from-emerald-500 to-teal-600",
      icon: Shield,
      coverage: "200+ countries",
      plans: "From $4.50 / 1GB",
      highlights: ["Instant QR activation", "24/7 support", "4.6 star rating"],
    },
    {
      name: "Holafly",
      url: "https://www.holafly.com",
      description: "Unlimited data plans. Best for heavy users and long trips.",
      tag: "Biggest Scope",
      tagColor: "bg-violet-500/20 text-violet-400 border-violet-500/30",
      iconBg: "bg-gradient-to-br from-violet-500 to-purple-600",
      icon: Wifi,
      coverage: "170+ destinations",
      plans: "From $6/day unlimited",
      highlights: ["Unlimited data", "No speed throttling", "Multi-device sharing"],
    },
    {
      name: "Nomad eSIM",
      url: "https://www.getnomad.app",
      description: "Budget-friendly eSIM. Lowest prices for data-light travelers.",
      tag: "Cheapest",
      tagColor: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      iconBg: "bg-gradient-to-br from-amber-500 to-orange-600",
      icon: Zap,
      coverage: "100+ countries",
      plans: "From $3 / 500MB",
      highlights: ["Ultra-low prices", "Pay-as-you-go", "Simple setup"],
    },
    {
      name: "aloSIM",
      url: "https://www.alosim.com",
      description: "Clean UX, transparent pricing. Great for first-time eSIM users.",
      tag: "Easy Setup",
      tagColor: "bg-sky-500/20 text-sky-400 border-sky-500/30",
      iconBg: "bg-gradient-to-br from-sky-500 to-blue-600",
      icon: Smartphone,
      coverage: "170+ countries",
      plans: "From $4.50 / 1GB",
      highlights: ["Beginner-friendly", "No hidden fees", "Instant delivery"],
    },
    {
      name: "Ubigi",
      url: "https://www.ubigi.com",
      description: "Premium global connectivity. Partnered with major carriers worldwide.",
      tag: "Premium",
      tagColor: "bg-rose-500/20 text-rose-400 border-rose-500/30",
      iconBg: "bg-gradient-to-br from-rose-500 to-pink-600",
      icon: Signal,
      coverage: "190+ countries",
      plans: "From $5 / 1GB",
      highlights: ["Carrier-grade quality", "Auto-connect roaming", "Business plans available"],
    },
  ];

  const [expanded, setExpanded] = useState(null);

  return (
    <div className="page-container p-4 pt-6 pb-28" data-testid="travel-esim-page">
      <div className="max-w-2xl mx-auto">
        {/* Hero Banner */}
        <div className="relative h-36 sm:h-44 lg:h-52 overflow-hidden -mx-4 -mt-6 mb-6 rounded-b-2xl">
          <img src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1200&q=80" alt="" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080d19]/80 via-[#080d19]/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 pb-5">
            <h1 className="text-2xl font-bold font-heading text-white drop-shadow-lg">Travel eSIM</h1>
            <p className="text-sm text-slate-300/80 mt-0.5 drop-shadow">Stay connected anywhere in the world</p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mb-5 p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-start gap-2">
          <Info className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-cyan-300 leading-relaxed">
            An eSIM is a digital SIM — no physical card needed. Check if your phone supports eSIM before purchasing. Most phones from 2020+ support it.
          </p>
        </div>

        {/* Provider Cards */}
        <div className="space-y-3">
          {providers.map((p) => {
            const Icon = p.icon;
            const isExpanded = expanded === p.name;
            return (
              <div
                key={p.name}
                className="bg-slate-900/70 rounded-xl border border-white/5 overflow-hidden transition-all hover:border-white/10"
                data-testid={`esim-provider-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <button
                  onClick={() => setExpanded(isExpanded ? null : p.name)}
                  className="w-full flex items-center gap-3 p-4 text-left"
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${p.iconBg}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-semibold text-white">{p.name}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${p.tagColor}`}>{p.tag}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{p.description}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-slate-500">{p.coverage}</p>
                    <p className="text-sm font-semibold text-white">{p.plans}</p>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3 border-t border-white/5 pt-3">
                    <p className="text-sm text-slate-300">{p.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {p.highlights.map((h) => (
                        <span key={h} className="text-xs px-2.5 py-1 rounded-lg bg-white/5 text-slate-300 border border-white/8">
                          {h}
                        </span>
                      ))}
                    </div>
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-white font-medium transition-colors border border-white/10"
                      data-testid={`esim-visit-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      Visit {p.name} <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Comparison Table */}
        <div className="mt-6 bg-slate-900/70 rounded-xl border border-white/5 overflow-hidden" data-testid="esim-comparison">
          <div className="p-4 border-b border-white/5">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-400" /> Quick Comparison
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-slate-400 font-medium px-4 py-2">Provider</th>
                  <th className="text-center text-slate-400 font-medium px-3 py-2">Countries</th>
                  <th className="text-center text-slate-400 font-medium px-3 py-2">Cheapest</th>
                  <th className="text-center text-slate-400 font-medium px-3 py-2">Unlimited</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/3"><td className="px-4 py-2 text-white font-medium">Airalo</td><td className="px-3 py-2 text-center text-slate-300">200+</td><td className="px-3 py-2 text-center text-slate-300">$4.50</td><td className="px-3 py-2 text-center text-red-400">No</td></tr>
                <tr className="border-b border-white/3"><td className="px-4 py-2 text-white font-medium">Holafly</td><td className="px-3 py-2 text-center text-slate-300">170+</td><td className="px-3 py-2 text-center text-slate-300">$6/day</td><td className="px-3 py-2 text-center text-emerald-400">Yes</td></tr>
                <tr className="border-b border-white/3"><td className="px-4 py-2 text-white font-medium">Nomad</td><td className="px-3 py-2 text-center text-slate-300">100+</td><td className="px-3 py-2 text-center text-emerald-400">$3</td><td className="px-3 py-2 text-center text-red-400">No</td></tr>
                <tr className="border-b border-white/3"><td className="px-4 py-2 text-white font-medium">aloSIM</td><td className="px-3 py-2 text-center text-slate-300">170+</td><td className="px-3 py-2 text-center text-slate-300">$4.50</td><td className="px-3 py-2 text-center text-red-400">No</td></tr>
                <tr><td className="px-4 py-2 text-white font-medium">Ubigi</td><td className="px-3 py-2 text-center text-slate-300">190+</td><td className="px-3 py-2 text-center text-slate-300">$5</td><td className="px-3 py-2 text-center text-red-400">No</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 p-3 rounded-xl bg-white/[0.03] border border-white/5">
          <p className="text-[11px] text-slate-500 text-center">
            Prices are approximate and may vary by region. Always check the provider's website for the latest plans.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TravelEsim;
